// Navigation: labelled groups (rail v2, 02/11/2026: Today / Work / People /
// Money / Company), mixed-case items, white-pill active state. Every screen is now built against real data (Suppliers/Expenses show
// honest empty states until their data sources exist). `roles` keeps our role
// gating: money screens are hidden from non-finance roles (rules are the real
// boundary regardless).
import { navAllowed, hasFeature, FEATURE_KEYS } from './features.js';
import { orgInfo } from './org-info.js';
import { canAssets } from './assets-access.js';

function featureOn(key) {
  if (FEATURE_KEYS.includes(key)) return hasFeature(key);
  const f = (orgInfo().features || {});
  return f[key] !== false;
}

const FINANCE = ['Owner', 'Director', 'Finance'];
const MONEY = ['Owner', 'Director', 'Finance', 'Coordinator'];
// Files + Mail ran as a four-person pilot from 05/08 (a per-USER gate, not a
// role — the four spanned Owner / Director+Finance / Coordinator, so no role set
// described them). GRADUATED 12/09/2026: the audit found nine of thirteen staff
// could not see two finished modules, by sidebar or by URL, five weeks after the
// pilot was meant to prove them. The gate is gone rather than widened — a list
// of names in the navigation is a thing nobody remembers to delete.
//
// Mail still cannot SEND (no gmail.send scope is requested) and several Files
// actions are stubs; both are honest about that on screen, which is the right
// way round. Hiding a working register from the people who need it is not.

// CALCULATE IS KARAN'S ALONE WHILE IT IS BUILT (Karan 05/10/2026). The same
// list is in firestore.rules (calcPilot) — the rail hides it, the rules are what
// refuse it. Widening it is Karan's call, and when it graduates this list and
// the rules function go together, not one without the other.
// Steve has the same Calculate permissions as Karan (Karan 12/10/2026).
export const CALCULATE_PILOT = ['karan@aspconsulting.co.uk', 'steve@aspconsulting.co.uk'];
// The checking engineer (REVIEW-MODE §1): sees the Calculate sandbox and any
// live package he is added to, and nothing else in the rail. Same list as
// firestore.rules calcReviewer() and functions/calc.js CALC_REVIEWERS.
export const CALC_REVIEWERS = [];
export function isCalcReviewer(email) {
  return CALC_REVIEWERS.includes(String(email || '').toLowerCase());
}
export function canCalculate(email) {
  return CALCULATE_PILOT.includes(String(email || '').toLowerCase()) || isCalcReviewer(email);
}

// REPORTS (Karan 28/10/2026): the feature `reports` AND the company's own list of
// people, settings.reports (functions/reports.js setReportsUsers) — at go-live
// Karan and Steve only. Filled at sign-in from the org document (org.js); the
// rules (reportsOn) and every Reports callable check the same list.
export const REPORTS_EMAILS = [];
export function setReportsAccess(r) {
  REPORTS_EMAILS.length = 0;
  for (const e of (r && Array.isArray(r.emails) ? r.emails : [])) if (typeof e === 'string' && e.includes('@')) REPORTS_EMAILS.push(e.toLowerCase());
}
export function canReports(email) {
  return hasFeature('reports') && REPORTS_EMAILS.includes(String(email || '').toLowerCase());
}

// ── THE ASP RAIL: Today · Work · People · Money · Company (rail v2, Karan 02/11/2026;
// docs/rail-v2/BRIEF.md, the design rail.png). ONE list for everyone — the Finance
// FY 2026–27 pilot no longer decides the rail's shape (the old NAV_GROUPS_FY27 is
// gone): Expenses, Overview and Accounts are in the rail for everyone the roles
// allow. fin-pilot.js still decides who gets the FY27 SCREENS behind those items.
//
// Nothing is deleted and no URL dies: a folded screen keeps its id and route as a
// TAB of the item it lives under (`tab`, hidden in the rail, still resolved by
// parsePath), and every hidden item sits in the group its parent is in, so
// railParent() and the header's group prefix resolve. test/rail-v2.test.mjs
// lists every id that existed before the reorganisation.
//   Today    the personal daily four
//   Work     the pipeline, front door (D1) first
//   People   the address book and the team
//   Money    FINANCE roles; Expenses has no role gate (everyone claims)
//   Company  pinned to the foot (.nav-group-foot), with its label
export const ACCOUNTS_TABS = ['pl', 'budget', 'cashflow', 'vat', 'loan'];
const ACCOUNTS_LABEL = { pl: 'P&L', budget: 'Budget', cashflow: 'Cash flow', vat: 'VAT', loan: 'Loan' };

export const NAV_GROUPS = [
  { label: null, items: [{ id: 'home', label: 'Home', enabled: true, built: true }] },
  {
    label: 'Today',
    items: [
      { id: 'tasks', label: 'Tasks', enabled: true, built: true },
      { id: 'time', label: 'Time', enabled: true, built: true },
      { id: 'calendar', label: 'Calendar', enabled: true, built: true },
      { id: 'mail', label: 'Mail', enabled: true, built: true },
      // Tabs of Time. Approvals keeps its MONEY gate; its count is shown on Time
      // (it IS a Time tab — the one roll-up the rail makes, rail-badges.js).
      { id: 'approvals', label: 'Approvals', enabled: true, built: true, roles: MONEY, hidden: true, tab: 'time' },
      // CPD (v4.23.0, Karan 29/10/2026): everyone's own log, a tab of Time. The
      // team's hours are Team › CPD (Owner and Directors).
      { id: 'cpd', label: 'CPD', enabled: true, built: true, hidden: true, tab: 'time' },
    ],
  },
  {
    label: 'Work',
    items: [
      { id: 'enquiries', label: 'Enquiries', enabled: true, built: true, roles: MONEY }, // fees visible → Coordinator+ (Karan 10/07)
      { id: 'projects', label: 'Projects', enabled: true, built: true },
      { id: 'files', label: 'Files', enabled: true, built: true },
      // REPORTS (v4.22.2, Karan 29/10/2026): the feature `reports` AND the company's
      // list (settings.reports), exactly as canReports: `emails` is the same live array.
      { id: 'reports', label: 'Reports', enabled: true, built: true, emails: REPORTS_EMAILS },
      { id: 'calculate', label: 'Calculate', enabled: true, built: true, emails: [...CALCULATE_PILOT, ...CALC_REVIEWERS] },
      // Calculate users only (the route checks canCalculate too): the Tedds parallel runs.
      { id: 'calc-verify', label: 'Calculate audit', enabled: true, built: true, hidden: true },
      // Settings › Reports (RpTemplates): the company's report types and their standard
      // wording. Owner and Directors on the Reports list.
      { id: 'report-types', label: 'Report types', enabled: true, built: true, hidden: true, roles: ['Owner', 'Director'], emails: REPORTS_EMAILS },
      // A report at a desk (Reports phase B3, RpReview): /report/{projectId}~{reportId}. The Reports list only.
      { id: 'report', label: 'Report', enabled: true, built: true, hidden: true, emails: REPORTS_EMAILS },
      // A structural survey on a PC (SvMatrix, Reports B2): /report-survey/<project>.<report>.
      { id: 'report-survey', label: 'Structural survey', enabled: true, built: true, hidden: true, emails: REPORTS_EMAILS },
    ],
  },
  {
    label: 'People',
    items: [
      { id: 'clients', label: 'Clients', enabled: true, built: true },
      { id: 'suppliers', label: 'Suppliers', enabled: true, built: true },
      { id: 'team', label: 'Team', enabled: true, built: true },
      { id: 'feedback', label: 'Feedback', enabled: true, built: true },
      // A tab of Feedback (Karan 08/10/2026): everyone sees their own figures, the
      // Owner and Directors everyone's.
      { id: 'responsiveness', label: 'Responsiveness', enabled: true, built: true, hidden: true, tab: 'feedback' },
    ],
  },
  {
    label: 'Money',
    items: [
      // `summary` keeps its id and /summary route (/overview is an alias); it reads Overview for everyone.
      { id: 'summary', label: 'Overview', enabled: true, built: true, roles: FINANCE },
      { id: 'invoices', label: 'Invoices', enabled: true, built: true, roles: FINANCE }, // Coordinators don't see invoices (Karan 14/07)
      { id: 'bank', label: 'Bank', enabled: true, built: true, roles: FINANCE },
      // Everyone claims: no role gate. Its own £ icon (`navIco`).
      { id: 'expenses', label: 'Expenses', enabled: true, built: true, navIco: 'pound' },
      { id: 'procurement', label: 'Purchase orders', enabled: true, built: true, roles: FINANCE },
      // Accounts, with its five tabs listed indented under it while it is open (`sub`).
      { id: 'accounts', label: 'Accounts', enabled: true, built: true, roles: FINANCE },
      ...ACCOUNTS_TABS.map((id) => ({ id, label: ACCOUNTS_LABEL[id], enabled: true, built: true, roles: FINANCE, hidden: true, tab: 'accounts', sub: true })),
    ],
  },
  {
    // The foot group (.nav-group-foot): pinned to the bottom of the rail, with its label.
    label: 'Company', foot: true,
    items: [
      // The catalogue carries fees + the charge-out rate (and Archive shows
      // closed-project fees), so it's gated to money-handling roles (Karan 05/08).
      { id: 'catalogue', label: 'Catalogue', enabled: true, built: true, roles: MONEY },
      { id: 'items', label: 'Items', enabled: true, built: true, roles: MONEY, hidden: true, tab: 'catalogue' },
      { id: 'services', label: 'Services', enabled: true, built: true, roles: MONEY, hidden: true, tab: 'catalogue' },
      { id: 'archive', label: 'Archive', enabled: true, built: true, roles: MONEY, hidden: true, tab: 'catalogue' },
      // Catalogue › Fee agent (v4.23.7, Karan 29/10/2026): the Owner and Directors, where the fee agent is on.
      { id: 'fee-agent', label: 'Fee agent', enabled: true, built: true, roles: ['Owner', 'Director'], hidden: true, tab: 'catalogue', feature: 'feeAgent' },
      // ASSETS (NFC asset tagging): gated on features.assets AND the company's list
      // (settings.assets.access, assets-access.js canAssets).
      { id: 'assets', label: 'Assets', enabled: true, built: true, gate: (email) => canAssets(email) },
      // Settings: the Owner (Company) and Directors (Email signature only, views/settings.js directorOnly).
      { id: 'settings', label: 'Settings', enabled: true, built: true, roles: ['Owner', 'Director'] },
      { id: 'help', label: 'Help', enabled: true, built: true },
      // Folded into Help, routes kept so old links and bookmarks still land.
      { id: 'costs', label: 'Costs', enabled: true, built: true, roles: FINANCE, hidden: true },
      { id: 'issues', label: 'Issues', enabled: true, built: true, roles: ['Owner', 'Director'], hidden: true },
      // Reached FROM Help, never from the rail: routable so a pasted link survives.
      { id: 'guide', label: 'Guide', enabled: true, built: true, hidden: true },
      { id: 'releases', label: 'Releases', enabled: true, built: true, hidden: true },
      { id: 'indexed', label: 'Indexed documents', enabled: true, built: true, hidden: true },
      { id: 'guides', label: 'Guides', enabled: true, built: true, hidden: true },
      { id: 'library', label: 'Ask Track', enabled: true, built: true, hidden: true },
      { id: 'office', label: 'Office Display', enabled: true, built: true, hidden: true }, // reachable at /office (for the TV), not shown in the sidebar
    ],
  },
];

// ── THE CONTRACTOR RAIL (Traverse, 21/10/2026; CONTRACTOR-SCREENS-HANDOFF §1) ──
// Same screens, same routes — a different rail for a different kind of company,
// chosen by organisations/{org}.template. ASP has no template, so ASP's rail
// above is the one drawn for ASP, unchanged.
//
// Roles are the contractor template's (§2): Owner · Office · Site lead · Operative.
// `ipad` puts an item on the 84px icon rail and orders it there; `site` does the
// same for the site set (Site lead, Operative), whose rail is Today · Projects ·
// Review · Measure · Spend with no More. `feature` names a module the Owner can
// switch off in Settings; until the backend registers the key it counts as on.
export const C_OFFICE = ['Owner', 'Office'];
const C_LEADS = ['Owner', 'Office', 'Site lead'];
export const CONTRACTOR_GROUPS = [
  {
    label: 'Work',
    items: [
      { id: 'home', label: 'Today', enabled: true, built: true, ipad: 1, site: 1, icon: 'today', line: 'Your day, what needs you' },
      { id: 'projects', label: 'Projects', enabled: true, built: true, ipad: 2, site: 2, icon: 'projects', line: 'Every project, quoted against actual' },
      { id: 'tasks', label: 'Tasks', enabled: true, built: true, ipad: 3, site: 3, siteLabel: 'Review', icon: 'review', feature: 'taskReview', line: 'Work waiting for sign-off' },
      { id: 'time', label: 'Time', enabled: true, built: true, roles: C_LEADS, ipad: 4, icon: 'time', feature: 'checkin', line: 'Check-ins, timesheets, lock the week' },
      { id: 'measure', label: 'Measure', enabled: true, built: true, hidden: true, site: 4, icon: 'measure', feature: 'siteMeasure', line: 'Measure on a drawing or on site' },
    ],
  },
  {
    label: 'Money',
    items: [
      { id: 'quotes', label: 'Quotes', enabled: true, built: true, roles: C_LEADS, ipad: 5, icon: 'quote', feature: 'quotes', line: 'Build, send and renew quotes' },
      { id: 'invoices', label: 'Invoices', enabled: true, built: true, roles: C_OFFICE, icon: 'invoice', feature: 'invoices', line: 'Raise and chase invoices' },
      { id: 'procurement', label: 'Purchase orders', enabled: true, built: true, roles: C_OFFICE, icon: 'order', line: 'Order from suppliers' },
      { id: 'expenses', label: 'Expenses', enabled: true, built: true, site: 5, siteLabel: 'Spend', icon: 'spend', line: 'Receipts from site' },
    ],
  },
  {
    label: 'Records',
    items: [
      { id: 'clients', label: 'Clients', enabled: true, built: true, roles: C_LEADS, icon: 'clients', line: 'Who you work for' },
      { id: 'suppliers', label: 'Suppliers', enabled: true, built: true, roles: C_OFFICE, icon: 'suppliers', line: 'Who you buy from' },
      { id: 'catalogue', label: 'Catalogue', enabled: true, built: true, roles: C_OFFICE, ipad: 6, icon: 'catalogue', line: 'Materials, labour and build-ups' },
      { id: 'price-review', label: 'Price review', enabled: true, built: false, roles: C_OFFICE, hidden: true, more: 'Records', feature: 'catalogue', icon: 'catalogue', line: 'This month’s material prices' },
      { id: 'team', label: 'Team', enabled: true, built: true, roles: C_OFFICE, ipad: 7, icon: 'team', line: 'People, approvers and pay rates' },
    ],
  },
  {
    label: null, settings: true,
    items: [
      { id: 'settings', label: 'Settings', enabled: true, built: false, roles: ['Owner'], icon: 'settings', more: 'Your company', line: 'Numbering, email, purchase orders and more' },
    ],
  },
  {
    label: '', foot: true,
    items: [
      { id: 'help', label: 'Help', enabled: true, built: true, icon: 'help', more: 'Your company', line: 'Walkthroughs and how Track works' },
      // The iPad's eighth slot: every page, in groups (TMore). Never on the laptop rail.
      { id: 'more', label: 'More', enabled: true, built: true, hidden: true, ipad: 8, icon: 'more' },
      { id: 'guide', label: 'Guide', enabled: true, built: true, hidden: true },
      { id: 'releases', label: 'Releases', enabled: true, built: true, hidden: true },
    ],
  },
];
const SITE_ROLES = ['Site lead', 'Operative'];
/** The site set: a Site lead or Operative who is not also Owner/Office. */
export const isSiteRole = (roles = []) => roles.some((r) => SITE_ROLES.includes(r)) && !roles.some((r) => C_OFFICE.includes(r));

let GROUPS = NAV_GROUPS;
let ALL = NAV_GROUPS.flatMap((g) => g.items);
/** Which rail this organisation gets ('contractor' | anything else = ASP's). */
export function useTemplate(template) {
  GROUPS = template === 'contractor' ? CONTRACTOR_GROUPS : NAV_GROUPS;
  ALL = GROUPS.flatMap((g) => g.items);
}
export const activeGroups = () => GROUPS;
export function navItem(id) { return ALL.find((n) => n.id === id) || null; }
// An item is visible when its role gate passes AND (if it carries an `emails`
// allowlist) the signed-in email is on it. `email` is optional so existing
// role-only call sites keep working; a pilot item with no email simply fails.
// The edit-patterns pilot (inline editing, the row menu, the change list) rode
// on the Files gate because the same four people were evaluating both. Lifting
// the Files gate would have switched inline editing on for the whole practice as
// a side effect, which is not a decision a navigation change should make — so it
// has its own list now, and graduating it is its own call (Karan 12/09).
const EDIT_PATTERNS_PILOT = ['karan@aspconsulting.co.uk', 'sanj@aspconsulting.co.uk',
  'ahmed@aspconsulting.co.uk', 'solomon@aspconsulting.co.uk'];
export function canEditInline(email) {
  return EDIT_PATTERNS_PILOT.includes(String(email || '').toLowerCase());
}

/**
 * The rail item a screen belongs to. A folded screen (Approvals, Items, …) is a
 * TAB of another item now, so the rail highlights the parent while the URL and
 * the view id stay its own.
 */
export function railParent(id) {
  const it = navItem(id);
  return (it && it.tab) || id;
}
/** The tabs under one rail item, in order, role-filtered. */
export function tabsFor(parentId, roles, email) {
  return ALL.filter((it) => it.tab === parentId && canSee(it, roles, email));
}

export function canSee(item, roles, email) {
  // The organisation's features first (multi-tenant step 4): a screen whose
  // module is switched off for this org is not in the rail and not routable.
  if (!navAllowed(item.id)) return false;
  // A contractor module must be switched on. A key the backend has registered
  // goes through hasFeature; one it has not yet is read from the org's own
  // switches, where only an explicit false turns it off (CONTRACT.md, UI needs).
  if (item.feature && !featureOn(item.feature)) return false;
  // A reviewer keeps his full rail: Steve is ASP staff and works in Track as
  // well as checking Calculate (Karan 12/10). Calculate's own data is bounded by
  // the rules (calcReviewer), not by the rail.
  if (item.roles && !item.roles.some((r) => roles.includes(r))) return false;
  if (item.emails && !(email && item.emails.includes(String(email).toLowerCase()))) return false;
  // A per-person gate (Assets: settings.assets.access, assets-access.js canAssets).
  if (item.gate && !item.gate(email)) return false;
  return true;
}

/**
 * The rail this person sees: hidden items and the items their roles, lists and
 * the company's features refuse are dropped, and a group left with nothing is
 * not drawn at all — no empty label, no greyed rows (rail v2 §4).
 */
export function visibleGroups(roles, email) {
  return GROUPS
    .map((g) => ({ label: g.label, foot: !!g.foot, items: g.items.filter((it) => canSee(it, roles, email) && !it.hidden) }))
    .filter((g) => g.items.length);
}
